/**
 * SOLID Review
 * S — Strong. One use case: the re-acceptance gate — the documents a signed-in player must accept again, or none.
 * O — Strong. Which documents need accepting is the Domain's rule.
 * L — Strong. Implements its use-case role exactly.
 * I — Strong. `execute`.
 * D — Strong. The decisions and legal-server ports and the session's step, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { ICommandNothing } from '../../domain/ports/schemas/TableMoves';
import type { IEntityLegalReview, ICommandPlanLegalReview, ICommandLegalReviewOf } from '../../domain/ports/schemas/LegalMoves';
import type { TPortResult } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { IConfigDepLegal } from './IConfigDepLegal';

const NO_REVIEW: IEntityLegalReview = { review: [] };

/**
 * THE RE-ACCEPTANCE GATE (FE4a). It FAILS OPEN, as it did: a status or document read that fails reads as "nothing to accept"
 * — a transient error must not lock a player out of the game.
 */
export class CheckLegalReviewUseCase implements IUseCase<ICommandNothing, TPortResult<IEntityLegalReview>> {
  constructor(private readonly deps: IConfigDepLegal) {}

  async execute(ctx: IContextRequest, _command: ICommandNothing): Promise<TPortResult<IEntityLegalReview>> {
    const { decisions, step, legal } = this.deps;
    const status = await legal.consentStatus(ctx);
    if (!status.success) return { success: true, value: NO_REVIEW };
    const planned: ICommandPlanLegalReview = { status: status.value };
    const plan = await step.run(ctx, (input) => decisions.planLegalReview(ctx, input, planned));
    if (!plan.success) return plan;
    if (!plan.value.fetchDocuments) return { success: true, value: NO_REVIEW };
    const documents = await legal.documents(ctx);
    if (!documents.success) return { success: true, value: NO_REVIEW };
    const loaded: ICommandLegalReviewOf = { documents: documents.value.documents };
    return step.run(ctx, (input) => decisions.legalReviewOf(ctx, input, loaded));
  }
}
